/**
 * ExportDropdown — Phase G4C CP2.
 *
 * Renders on `/proposals/{id}/draft` beside the header. Provides
 * PDF / DOCX / TXT downloads with per-format loading state, inline
 * error banners (NEVER toasts), and Free-tier upgrade CTA.
 *
 * On success: uses the response's `Content-Disposition` filename
 * verbatim (never renamed on the client). Streams to an object URL
 * → hidden `<a download>` click → revoke URL.
 *
 * Response-code discrimination:
 *   - 200                              → download
 *   - 402 `proposals_premium_only`     → inline Premium upgrade CTA
 *   - 404 `feature_disabled`           → inline FeatureDisabled row
 *   - 409 `export_archived`            → inline banner
 *   - 422 `no_exportable_sections`    → inline "nothing to export yet"
 *   - 422 `invalid_section_shape`     → inline retryable banner
 *   - Everything else                  → inline retryable banner
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  Loader2,
  ChevronDown,
  AlertCircle,
  FileText,
  Info,
} from "lucide-react";

import { api, formatApiError } from "@/api/client";
import { filenameFromContentDisposition } from "./quality_helpers";
import {
  EXPORT_TRIGGER_LABEL,
  EXPORT_FORMAT_LABELS,
  EXPORT_NO_SECTIONS_TITLE,
  EXPORT_NO_SECTIONS_BODY,
  EXPORT_FAILURE_BANNER_TITLE,
  EXPORT_FAILURE_BANNER_BODY_FALLBACK,
  FREE_TIER_EXPORT_TITLE,
  FREE_TIER_EXPORT_BODY,
  UPGRADE_CTA_LABEL,
  FEATURE_DISABLED_TITLE,
  FEATURE_DISABLED_BODY,
} from "./quality_copy";


const FORMATS = ["pdf", "docx", "txt"];


function _mimeFor(fmt) {
  return {
    pdf: "application/pdf",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    txt: "text/plain",
  }[fmt];
}


function _fallbackFilename(proposalId, fmt) {
  return `proposal_${proposalId}.${fmt}`;
}


export default function ExportDropdown({ proposalId, isFreeTier = false }) {
  const [open, setOpen] = useState(false);
  const [busyFormat, setBusyFormat] = useState(null);
  const [banner, setBanner] = useState(null);  // {kind, title, body}
  const wrapRef = useRef(null);

  // Close on click-outside.
  useEffect(() => {
    if (!open) return undefined;
    function _onClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", _onClick);
    return () => document.removeEventListener("mousedown", _onClick);
  }, [open]);

  const doDownload = useCallback(
    async (fmt) => {
      if (!proposalId || busyFormat) return;
      setBusyFormat(fmt);
      setBanner(null);
      try {
        const res = await api.get(`/proposals/${proposalId}/export`, {
          params: { format: fmt },
          responseType: "blob",
        });

        // Extract filename verbatim from Content-Disposition; safe
        // fallback prevents an accidental blank-filename download.
        const headerFilename = filenameFromContentDisposition(
          res.headers?.["content-disposition"] ||
            res.headers?.["Content-Disposition"] ||
            "",
        );
        const filename = headerFilename || _fallbackFilename(proposalId, fmt);

        const blob = new Blob([res.data], { type: _mimeFor(fmt) });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        // Close the dropdown after successful download.
        setOpen(false);
      } catch (err) {
        // axios blobbed the error body; parse manually.
        let code = null;
        let message = null;
        try {
          if (err?.response?.data instanceof Blob) {
            const text = await err.response.data.text();
            const parsed = JSON.parse(text);
            code = parsed?.detail?.code;
            message = parsed?.detail?.message;
          } else {
            code = err?.response?.data?.detail?.code;
            message = err?.response?.data?.detail?.message;
          }
        } catch (_e) {
          /* fall through to generic error */
        }
        if (code === "no_exportable_sections") {
          setBanner({
            kind: "warning",
            title: EXPORT_NO_SECTIONS_TITLE,
            body: EXPORT_NO_SECTIONS_BODY,
            code,
          });
        } else if (code === "export_archived") {
          setBanner({
            kind: "warning",
            title: "This proposal is archived",
            body:
              message ||
              "Archived proposals cannot be exported. Duplicate or restore this proposal first.",
            code,
          });
        } else if (code === "proposals_premium_only") {
          setBanner({
            kind: "warning",
            title: FREE_TIER_EXPORT_TITLE,
            body: FREE_TIER_EXPORT_BODY,
            code,
          });
        } else if (code === "feature_disabled") {
          setBanner({
            kind: "warning",
            title: FEATURE_DISABLED_TITLE,
            body: FEATURE_DISABLED_BODY,
            code,
          });
        } else {
          setBanner({
            kind: "error",
            title: EXPORT_FAILURE_BANNER_TITLE,
            body: message || formatApiError(err) || EXPORT_FAILURE_BANNER_BODY_FALLBACK,
            code: code || "unknown",
          });
        }
      } finally {
        setBusyFormat(null);
      }
    },
    [proposalId, busyFormat],
  );

  // Free tier — replace the whole dropdown with an inline upgrade CTA.
  if (isFreeTier) {
    return (
      <div
        data-testid="export-free-tier"
        className="rounded-2xl border border-v2-border bg-v2-surface/60 px-4 py-3 flex items-center gap-3 flex-wrap"
      >
        <Info className="w-4 h-4 text-v2-accent flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-v2-caption uppercase tracking-[0.14em] text-v2-accent">
            PREMIUM FEATURE
          </p>
          <p className="text-v2-body-sm text-v2-textMuted">
            {FREE_TIER_EXPORT_BODY}
          </p>
        </div>
        <Link
          to="/billing"
          data-testid="export-upgrade-cta"
          className="inline-flex items-center gap-1 rounded-full bg-v2-accent px-3 py-1.5 text-v2-caption font-semibold text-v2-accentInk hover:bg-v2-accentHover transition-colors"
        >
          {UPGRADE_CTA_LABEL}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3" ref={wrapRef}>
      <div className="relative inline-block">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={busyFormat !== null}
          data-testid="export-trigger"
          aria-haspopup="menu"
          aria-expanded={open}
          className="inline-flex items-center gap-2 rounded-full border border-v2-accent px-4 py-2 text-v2-body-sm font-semibold text-v2-accent hover:bg-v2-accent/10 transition-colors disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {EXPORT_TRIGGER_LABEL}
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
        {open && (
          <div
            data-testid="export-menu"
            role="menu"
            className="absolute right-0 mt-2 w-56 rounded-2xl border border-v2-border bg-v2-surface shadow-xl overflow-hidden z-30"
          >
            {FORMATS.map((fmt) => {
              const busy = busyFormat === fmt;
              return (
                <button
                  key={fmt}
                  type="button"
                  onClick={() => doDownload(fmt)}
                  disabled={busy}
                  data-testid={`export-format-${fmt}`}
                  role="menuitem"
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-v2-body-sm text-v2-text hover:bg-v2-accent/10 transition-colors text-left disabled:opacity-70"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin text-v2-accent" />
                  ) : (
                    <FileText className="w-4 h-4 text-v2-accent" />
                  )}
                  <span className="min-w-0 flex-1">
                    {EXPORT_FORMAT_LABELS[fmt]}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {banner && (
        <div
          data-testid={
            banner.code === "no_exportable_sections"
              ? "export-banner-no-sections"
              : banner.kind === "error"
                ? "export-banner-error"
                : "export-banner-warning"
          }
          className="rounded-2xl border p-4 flex items-start gap-3"
          style={
            banner.kind === "error"
              ? {
                  background: "var(--v2-danger-soft)",
                  borderColor: "var(--v2-danger)",
                  color: "var(--v2-danger)",
                }
              : {
                  background: "rgba(245,158,11,0.10)",
                  borderColor: "rgba(245,158,11,0.45)",
                  color: "var(--v2-warning, #f59e0b)",
                }
          }
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 space-y-1">
            <h4 className="text-v2-body font-semibold">{banner.title}</h4>
            {banner.body && <p className="text-v2-body-sm">{banner.body}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
